import { useLazyQuery } from '@apollo/client';
import { AnalyticIdentifier, formatArrayToCommaAndString } from '@common/neo.tax';
import {
  Alert,
  Button,
  colors,
  ScreenLoading,
  SplitView,
  Stack,
  Typography,
  useModal,
  useSplitView,
} from '@ui-components/neo.tax';
import { ReactComponent as ShrinkIcon } from '@ui-components/neo.tax/dist/icons/shrink.svg';
import { useCallback, useEffect, useState } from 'react';

import { filterableRdProjectColumns } from '@neo.tax/rd/constants';
import { useRdContext } from '@neo.tax/rd/context';
import {
  useCreateRdProject,
  useMergeRdProjectsMutation,
  useRdProjectIdOptional,
  useRdProjects,
  useRdProjectsSummary,
  useGenerateRdProjectsReport,
  useGetAndSaveRdProjectsReportSignedUrl,
} from '@neo.tax/rd/hooks';
import { type RdProject } from '@neo.tax/rd/hooks/useRdProjects';
import { mapStringToSortField } from '@neo.tax/rd/utils';
import {
  ColumnVisibilityPopover,
  FilterPopover,
  FilterSortDataType,
  getPath,
  GetMergedRdProjectIdQuery,
  GetMergedRdProjectIdQueryVariables,
  GET_MERGED_RD_PROJECT_ID,
  HELP_FILL_OUT_RD_NARRATIVE_FIELDS_URL,
  isLocalEnvironment,
  KeywordSearch,
  Path,
  SortNulls,
  SortPopover,
  ToastConfigState,
  useAnalyticId,
  useBackgroundOperations,
  useColumnVisibility,
  useCurrentCompanyId,
  useCurrentUserScopeForCurrentCompany,
  useFilterConditions,
  useHasSkippedConnectingTicketing,
  useKeywordSearch,
  useNavigateTo,
  useSorting,
  RdProjectSortFields,
  SortDirection,
  MergeProjectsModal,
  ProjectCreationModal,
  ProjectsTable,
} from '@neo.tax/shared';
import { BackgroundOperationName } from '@neo.tax/shared/graphql';

import { RdLockPageLayout } from '../rd-lock-page-layout';
import { RdProjectBulkUploadModal } from '../rd-project-bulk-upload-modal';
import { RdProjectDetail } from '../rd-project-detail';
import { RegenerateRdProjectDetailsAction } from '../regenerate-rd-project-details-modal';
import { useRdProjectsAnalyticIds, useRdProjectsTableColumns } from './hooks';
import { MergeButton } from './rd-projects-layout.styled';

const PROJECT_DRAWER_WIDTH = 412;

export const RdProjectsLayout = (): React.ReactElement => {
  const { fiscalYearId, isNowPastPeriodEndDate } = useRdContext();
  const navigate = useNavigateTo();
  const companyId = useCurrentCompanyId();
  const { limitAccessToFinancialData } = useCurrentUserScopeForCurrentCompany();
  const rdProjectId = useRdProjectIdOptional();
  const { fixedWidth, isOpen, setIsOpen } = useSplitView({
    initialOpen: !!rdProjectId,
    fixedWidth: PROJECT_DRAWER_WIDTH,
  });

  const { isOpen: projectCreationModalOpen, setIsOpen: setProjectCreationModalIsOpen } = useModal();
  const { isOpen: uploadProjectsCsvModalOpen, setIsOpen: setUploadProjectsCsvModalIsOpen } = useModal();
  const { isOpen: isMergeProjectsModalOpen, setIsOpen: setIsMergeProjectsModalOpen } = useModal();
  const {
    cancelMergeProjectsAnalyticId,
    confirmMergeProjectsAnalyticId,
    createRdProjectAnalyticId,
    mergeProjectsAnalyticId,
  } = useRdProjectsAnalyticIds();
  const downloadLinkId = useAnalyticId(AnalyticIdentifier.ENTERPRISE_DOWNLOAD_RD_CREDITS_REPORT_LINK);
  const { hasSkippedConnectingTicketing } = useHasSkippedConnectingTicketing();

  const baseVisibilityColumns = [
    { name: 'name', label: 'Name', dataType: FilterSortDataType.TEXT },
    { name: 'productName', label: 'Product', dataType: FilterSortDataType.TEXT },
    { name: 'rdCategory', label: 'Selected Category', dataType: FilterSortDataType.TEXT },
    { name: 'aiGuidance', label: 'AI Guidance', dataType: FilterSortDataType.TEXT },
    { name: 'rdDetails', label: 'R&D Details', dataType: FilterSortDataType.TEXT },
    { name: 'owner', label: 'Owner', dataType: FilterSortDataType.TEXT },
  ] as const;

  const ticketingVisibilityColumns = [
    { name: 'teams', label: 'Teams', dataType: FilterSortDataType.TEXT },
    { name: 'tickets', label: 'Tickets', dataType: FilterSortDataType.NUMBER },
  ] as const;

  const allVisibilityColumns = hasSkippedConnectingTicketing
    ? baseVisibilityColumns
    : ([...baseVisibilityColumns, ...ticketingVisibilityColumns] as const);

  const columnVisibilityState = useColumnVisibility({
    columns: allVisibilityColumns,
    alwaysVisibleColumns: ['name'],
    initialVisibleColumns: allVisibilityColumns.map((c) => c.name),
    persistenceKey: 'rdProjects',
  });

  const [propRdProjectId, setPropRdProjectId] = useState(rdProjectId);
  const [mergingBackgroundOperationId, setMergingBackgroundOperationId] = useState<string | null>(null);

  const searchKeywordState = useKeywordSearch();
  const filterState = useFilterConditions({ columns: filterableRdProjectColumns, persistenceKey: 'rdProjects' });
  const sortState = useSorting({ columns: filterableRdProjectColumns, persistenceKey: 'rdProjects' });
  const isLocal = isLocalEnvironment();
  const { downloadRdProjectsReport } = useGetAndSaveRdProjectsReportSignedUrl();
  const {
    called: generateProjectsCalled,
    generateRdProjectsReport,
    loading: generateProjectsLoading,
  } = useGenerateRdProjectsReport();

  const {
    isLoadingMore,
    loading: projectsLoading,
    onLoadMoreItems,
    rdProjects,
  } = useRdProjects({
    searchKeyword: searchKeywordState.searchKeyword,
    filterConditions: filterState.conditions,
    sorting: [
      ...sortState.sorts.map((s) => ({
        field: mapStringToSortField(s.column),
        direction: s.direction,
        nulls: SortNulls.NullsLast,
      })),
      // Add ID as secondary sort to ensure deterministic ordering for cursor pagination
      {
        field: RdProjectSortFields.Id,
        direction: SortDirection.Asc,
        nulls: SortNulls.NullsLast,
      },
    ],
  });

  const { loading: loadingSummary, rdProjectsSummary } = useRdProjectsSummary({
    filterConditions: filterState.conditions,
    searchKeyword: searchKeywordState.searchKeyword,
  });

  const [createRdProject, { loading: loadingCreateRdProject }] = useCreateRdProject();

  const [mergeRdProjects, { loading: loadingMergeRdProjects }] = useMergeRdProjectsMutation();
  const [getMergedProjectId] = useLazyQuery<GetMergedRdProjectIdQuery, GetMergedRdProjectIdQueryVariables>(
    GET_MERGED_RD_PROJECT_ID,
  );
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const { backgroundOperations, loading: loadingBackgroundOperations } = useBackgroundOperations();

  // Determine if a merge is running by checking the polled server-side background operations.
  const hasMergeInProgress =
    backgroundOperations?.some(
      (op) => op.name === BackgroundOperationName.MergeRdProjectsOperation && op.progressPercent < 100,
    ) ?? false;

  // A merge is running: either this session just started one (optimistic, bridges the 60s poll gap)
  // or the server poll reports one (survives reload / covers merges started elsewhere).
  const isMergeInProgress = !!mergingBackgroundOperationId || hasMergeInProgress;

  const disableMergeProjectsButton =
    loadingBackgroundOperations || // stay disabled until we know the server truth (closes the post-reload gap)
    selectedRows.length < 2 || // need at least two projects to merge
    loadingMergeRdProjects || // still merging from a previous attempt
    isMergeInProgress;

  const handleMergeProjects = async (): Promise<void> => {
    if (!rdProjects) {
      return;
    }
    // A merge is already running (server-derived, holds across reload). Warn instead of launching a
    // second merge the server would reject anyway.
    if (hasMergeInProgress) {
      ToastConfigState.pushToast({
        title: 'Merge already in progress',
        description: 'A project merge is still running. Please wait for it to finish before starting another.',
        severity: 'warning',
      });
      return;
    }

    const selectedRdProjectIds = new Set(selectedRows);
    const rdProjectsToMerge = rdProjects
      .filter((rdProject) => selectedRdProjectIds.has(rdProject.id))
      .map((rdProject) => `"${rdProject.name}"`);
    const projectNames = formatArrayToCommaAndString(rdProjectsToMerge);
    navigate(getPath({ path: Path.RD_PROJECTS, companyId, fiscalYearId }));

    try {
      const result = await mergeRdProjects({
        variables: {
          fiscalYearId,
          input: {
            rdProjectIds: selectedRows,
          },
        },
        context: {
          loadingToast: {
            title: 'Merge started',
            description: `${projectNames} are being merged. Neo.Tax is recalculating effort, project name and project description.`,
          },
          errorToast: {
            title: 'Failed to merge projects',
            description: 'An unexpected error happened while merging projects. Please try again.',
          },
        },
      });

      const mergeResult = result.data?.mergeRdProjects;

      if (mergeResult) {
        // Track the background operation ID to keep modal open while polling
        setMergingBackgroundOperationId(mergeResult.id);
        // The selected projects are being merged away — drop the stale selection so the button and table
        // stop referencing projects that will no longer exist once the merge completes.
        setSelectedRows([]);
      } else {
        console.error('No result returned from merge mutation');
        setSelectedRows([]);
        setIsMergeProjectsModalOpen(false);
      }
    } catch (error) {
      console.error('Failed to start merge:', error);
      setSelectedRows([]);
      setIsMergeProjectsModalOpen(false);
    }
  };

  const handleCreateRdProject = async ({ description, name }: { name: string; description: string }): Promise<void> => {
    const { data } = await createRdProject({
      variables: {
        fiscalYearId,
        createRdProjectInput: {
          name,
          description,
        },
      },
    });

    if (data) {
      const path = getPath({
        path: Path.RD_PROJECTS,
        companyId,
        fiscalYearId,
        rdProjectId: data.createRdProject.id,
      });
      navigate(path);
    }
  };

  const handleSelectTableRow = (checked: boolean, rdProject: RdProject): void => {
    setSelectedRows((prev) => (checked ? [...prev, rdProject.id] : prev.filter((id) => id !== rdProject.id)));
  };

  const handleSelectAllTableRows = (checked: boolean): void => {
    if (!rdProjects) {
      setSelectedRows([]);
      return;
    }
    setSelectedRows(checked ? rdProjects.map((rdProject) => rdProject.id) : []);
  };

  const handleFetchMergedProjectId = useCallback(
    async (backgroundOperationId: string): Promise<void> => {
      try {
        const result = await getMergedProjectId({
          variables: { fiscalYearId, backgroundOperationId },
        });
        const mergedProjectId = result.data?.getMergedRdProjectId;
        if (mergedProjectId) {
          const path = getPath({ path: Path.RD_PROJECTS, companyId, fiscalYearId, rdProjectId: mergedProjectId });
          navigate(path);
        }
        setSelectedRows([]);
        setIsMergeProjectsModalOpen(false);
        setMergingBackgroundOperationId(null);
      } catch (error) {
        console.error('Failed to get merged project ID:', error);
        setSelectedRows([]);
        setIsMergeProjectsModalOpen(false);
        setMergingBackgroundOperationId(null);
      }
    },
    [getMergedProjectId, companyId, fiscalYearId, navigate, setIsMergeProjectsModalOpen],
  );

  // Detects changes in projectId
  useEffect(() => {
    if (rdProjectId) {
      setPropRdProjectId(rdProjectId);
    }
    setIsOpen(!!rdProjectId);
  }, [rdProjectId, setIsOpen]);

  // Poll for merge completion and navigate to merged project
  useEffect(() => {
    if (!mergingBackgroundOperationId || !backgroundOperations) {
      return;
    }

    const mergeOperation = backgroundOperations.find((op) => op.id === mergingBackgroundOperationId);

    if (!mergeOperation) {
      return;
    }

    if (mergeOperation.progressPercent === 100) {
      handleFetchMergedProjectId(mergingBackgroundOperationId);
    }
  }, [mergingBackgroundOperationId, backgroundOperations, handleFetchMergedProjectId]);

  const handleTransitionEnd = useCallback(() => {
    setPropRdProjectId(rdProjectId);
  }, [rdProjectId]);

  const columns = useRdProjectsTableColumns({
    clickedRowId: propRdProjectId ?? undefined,
    footerData: rdProjectsSummary,
    onSelectAllRows: handleSelectAllTableRows,
    onSelectRow: handleSelectTableRow,
    rowCount: rdProjects ? rdProjects.length : 0,
    selectedRowIds: selectedRows,
    visibleColumns: columnVisibilityState.visibleColumns,
  });

  const handleExport = async (): Promise<void> => {
    if (isLocal) {
      await downloadRdProjectsReport(fiscalYearId);
      return;
    }
    if (!generateProjectsLoading) {
      ToastConfigState.pushToast({
        title: 'Generating R&D Projects Report',
        description: 'This may take a few minutes. You will receive an email when it is ready.',
      });
      await generateRdProjectsReport({ variables: { fiscalYearId } });
    }
  };

  if (projectsLoading && !rdProjects) {
    return <ScreenLoading />;
  }

  const isPeriodCurrentAndPartialYearAlert = !isNowPastPeriodEndDate && (
    <Alert
      severity="info"
      title="You're viewing a partial year. This is only an estimate. Please note that changes to projects and allocations will happen automatically when future data is resynced"
    />
  );
  return (
    <SplitView
      mainView={
        <RdLockPageLayout
          title="R&D Projects"
          alert={isPeriodCurrentAndPartialYearAlert}
          subtitle={
            <Typography fontSize="sm" color={colors.gray[500]}>
              Start by clicking on a project and qualifying its eligibility for the R&D credit with Neo.Tax&apos;s
              guidance! <br /> If you need additional help, visit our help page{' '}
              <Typography
                as="a"
                href={HELP_FILL_OUT_RD_NARRATIVE_FIELDS_URL}
                target="_blank"
                fontSize="sm"
                color={colors.primary[500]}
              >
                here
              </Typography>
              .
            </Typography>
          }
          disableIfOngoingSync={true}
          disableIfRdProjectsImportInProgress
          disableIfSupervisorAllocationInProgress
          disableIfProjectDetailsUpdateInProgress
          lockButtons={
            <Button
              size="xs"
              variant="white"
              rounded="regular"
              as="button"
              onClick={handleExport}
              disabled={generateProjectsLoading}
              title="Excel file will be sent to your email"
            >
              {generateProjectsLoading ? 'Requesting..' : 'Generate Projects Report'}
            </Button>
          }
          trailingContent={
            <>
              <Stack direction="column" alignItems="end" gap={1}>
                <Stack>
                  {!limitAccessToFinancialData && (
                    <Button
                      variant="primary"
                      size="base"
                      rounded="regular"
                      onClick={handleExport}
                      id={downloadLinkId}
                      disabled={generateProjectsLoading}
                    >
                      {generateProjectsLoading ? 'Requesting..' : 'View in Excel *'}
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    size="base"
                    rounded="regular"
                    onClick={(): void => setProjectCreationModalIsOpen(true)}
                    id={createRdProjectAnalyticId}
                  >
                    Create Project
                  </Button>
                  <Button
                    variant="white"
                    size="base"
                    rounded="regular"
                    onClick={(): void => setUploadProjectsCsvModalIsOpen(true)}
                  >
                    Upload CSV
                  </Button>
                </Stack>
                {/* Left-align the Excel helper text under the left edge of the button group */}
                <Stack direction="column" gap={1} fullWidth alignItems="start">
                  {generateProjectsCalled && !generateProjectsLoading && (
                    <Typography fontSize="xs" color={colors.primary[700]}>
                      Excel file is being generated.
                    </Typography>
                  )}
                  {!limitAccessToFinancialData && (
                    <Typography fontSize="xs" color={colors.gray[600]}>
                      * Excel file will be sent to your email address.
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </>
          }
          fullWidth
        >
          <Stack direction="column" gap={6} fullHeight>
            <Stack gap={5}>
              <KeywordSearch {...searchKeywordState} />
              <FilterPopover itemsDescription="projects" {...filterState} />
              <SortPopover itemsDescription="projects" {...sortState} />
              <ColumnVisibilityPopover {...columnVisibilityState} />
              <MergeButton
                size="lg"
                variant="primary"
                rounded="regular"
                id={mergeProjectsAnalyticId}
                startIcon={<ShrinkIcon />}
                disabled={disableMergeProjectsButton}
                onClick={(): void => setIsMergeProjectsModalOpen(true)}
              >
                {disableMergeProjectsButton ? 'Merge Projects' : `Merge Projects (${selectedRows.length})`}
              </MergeButton>
              <RegenerateRdProjectDetailsAction />
            </Stack>
            <ProjectsTable<RdProject>
              data={rdProjects || []}
              selectedProjectId={propRdProjectId ?? undefined}
              selectedRowIds={selectedRows}
              columns={columns}
              loadMoreItems={onLoadMoreItems}
              loadingRows={projectsLoading}
              loadingFooter={loadingSummary || isLoadingMore}
            />
          </Stack>
          <ProjectCreationModal
            isOpen={projectCreationModalOpen}
            onClose={(): void => setProjectCreationModalIsOpen(false)}
            submit={handleCreateRdProject}
            loading={loadingCreateRdProject}
          />
          <RdProjectBulkUploadModal
            isOpen={uploadProjectsCsvModalOpen}
            close={(): void => setUploadProjectsCsvModalIsOpen(false)}
            fiscalYearId={fiscalYearId}
            companyId={companyId}
          />
          <MergeProjectsModal
            isOpen={isMergeProjectsModalOpen || isMergeInProgress}
            loading={loadingMergeRdProjects || isMergeInProgress}
            setIsOpen={setIsMergeProjectsModalOpen}
            submit={handleMergeProjects}
            analyticIds={{
              confirmMergeProjectsId: confirmMergeProjectsAnalyticId,
              cancelMergeProjectsId: cancelMergeProjectsAnalyticId,
            }}
            copy={
              isMergeInProgress ? (
                <>
                  Please wait while we merge the selected projects.
                  <br /> This may take a few minutes.
                </>
              ) : (
                <>
                  Merge combines the selected projects and updates the title/description. This will also reset the R&D
                  Eligibility fields. Are you sure you want to merge these projects?
                </>
              )
            }
          />
        </RdLockPageLayout>
      }
      drawerView={propRdProjectId ? <RdProjectDetail rdProjectId={propRdProjectId} /> : null}
      isOpen={isOpen}
      fixedWidth={fixedWidth}
      onTransitionEnd={handleTransitionEnd}
    />
  );
};
